import { useRef, useCallback, useMemo, useLayoutEffect, useState } from "react";
import { Search, SearchX } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { Button, Input } from "@/components/ui/primitives";
import { StateCard } from "@/components/ui";
import { ChecklistListItem } from "./ChecklistListItem";
import { useTranslation } from "@/packages/utils";
import type { UIChecklistItem, ChecklistItemId } from "@/packages/types";

interface CategoryGroup {
  id: string;
  name: string;
  items: UIChecklistItem[];
}

interface ChecklistListPanelProps {
  categories: CategoryGroup[];
  allCategories?: CategoryGroup[];
  selectedItemId: ChecklistItemId | null;
  onSelectItem: (id: ChecklistItemId) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  statusCounts?: {
    all: number;
    missing: number;
    uploaded: number;
    approved: number;
  };
}

const STATUS_FILTERS = ["all", "MISSING", "UPLOADED", "APPROVED"] as const;

interface PillStyle {
  left: number;
  width: number;
}

export function ChecklistListPanel({
  categories,
  allCategories,
  selectedItemId,
  onSelectItem,
  statusFilter,
  onStatusFilterChange,
  searchQuery,
  onSearchChange,
  statusCounts,
}: ChecklistListPanelProps) {
  const { t } = useTranslation(["audits", "common"]);
  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [pill, setPill] = useState<PillStyle | null>(null);
  const [isInitialPill, setIsInitialPill] = useState(true);

  // Build flat list of item IDs for keyboard navigation
  const flatItemIds = useMemo(() => {
    const ids: ChecklistItemId[] = [];
    for (const cat of categories) {
      for (const item of cat.items) {
        ids.push(item.id);
      }
    }
    return ids;
  }, [categories]);

  // Build category progress data by merging with allCategories
  const categoryProgress = useMemo(() => {
    const map = new Map<string, { total: number; approved: number }>();
    if (allCategories) {
      for (const cat of allCategories) {
        const approved = cat.items.filter(
          (item) => item.status === "APPROVED",
        ).length;
        map.set(cat.id, { total: cat.items.length, approved });
      }
    }
    return map;
  }, [allCategories]);

  // Sliding pill measurement
  const setButtonRef = useCallback(
    (id: string) => (el: HTMLButtonElement | null) => {
      if (el) {
        buttonRefs.current.set(id, el);
      } else {
        buttonRefs.current.delete(id);
      }
    },
    [],
  );

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const button = buttonRefs.current.get(statusFilter);
    if (!button) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const left = buttonRect.left - containerRect.left;
    const width = buttonRect.width;

    setPill((prev) => {
      if (prev && prev.left === left && prev.width === width) return prev;
      return { left, width };
    });

    requestAnimationFrame(() => {
      setIsInitialPill(false);
    });
  }, [statusFilter]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown" && e.key !== "Enter") {
        return;
      }

      e.preventDefault();

      if (e.key === "Enter" && selectedItemId) {
        return;
      }

      const currentIndex = selectedItemId
        ? flatItemIds.indexOf(selectedItemId)
        : -1;

      let nextIndex: number;
      if (e.key === "ArrowDown") {
        nextIndex =
          currentIndex < flatItemIds.length - 1 ? currentIndex + 1 : 0;
      } else {
        nextIndex =
          currentIndex > 0 ? currentIndex - 1 : flatItemIds.length - 1;
      }

      const nextId = flatItemIds[nextIndex];
      if (nextId) {
        onSelectItem(nextId);

        // Scroll into view
        requestAnimationFrame(() => {
          const el = listRef.current?.querySelector(
            `[data-item-id="${nextId}"]`,
          );
          el?.scrollIntoView({ block: "nearest" });
        });
      }
    },
    [flatItemIds, selectedItemId, onSelectItem],
  );

  const handleClearFilters = useCallback(() => {
    onStatusFilterChange("all");
    onSearchChange("");
  }, [onStatusFilterChange, onSearchChange]);

  const filterLabels: Record<string, string> = {
    all: t("checklist.edit.filterAll"),
    MISSING: t("checklist.edit.filterMissing"),
    UPLOADED: t("checklist.edit.filterUploaded"),
    APPROVED: t("checklist.edit.filterApproved"),
  };

  const filterCounts: Record<string, number | undefined> = statusCounts
    ? {
        all: statusCounts.all,
        MISSING: statusCounts.missing,
        UPLOADED: statusCounts.uploaded,
        APPROVED: statusCounts.approved,
      }
    : {};

  let itemIndex = 0;

  return (
    <div className="flex h-full flex-col border-r border-border">
      {/* Filter bar */}
      <div className="space-y-2 border-b border-border p-2">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("checklist.edit.searchPlaceholder")}
            className="h-8 bg-muted/30 pl-7 text-sm"
          />
        </div>

        {/* Status filter toggle */}
        <div
          ref={containerRef}
          className="relative flex items-center gap-0.5 rounded-lg bg-muted/40 p-0.5"
        >
          {/* Sliding pill */}
          {pill && (
            <div
              className="absolute top-0.5 bottom-0.5 z-0 rounded-md bg-background shadow-sm"
              style={{
                left: pill.left,
                width: pill.width,
                transition: isInitialPill
                  ? "none"
                  : "left 200ms cubic-bezier(0.4, 0, 0.2, 1), width 200ms cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />
          )}
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter}
              ref={setButtonRef(filter)}
              type="button"
              onClick={() => onStatusFilterChange(filter)}
              className={`relative z-10 flex-1 rounded-md px-2 py-1 text-[11px] font-medium whitespace-nowrap transition-colors duration-150 ${
                statusFilter === filter
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {filterLabels[filter]}
              {filterCounts[filter] !== undefined && (
                <span className="ml-1 text-[10px] opacity-60">
                  {filterCounts[filter]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable list */}
      <div
        ref={listRef}
        role="listbox"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        className="flex-1 overflow-y-auto outline-none"
      >
        {categories.length === 0 ? (
          <div className="flex h-full items-center justify-center p-4">
            <StateCard
              variant="empty"
              className="w-full border-0 bg-transparent shadow-none"
            >
              <StateCard.Icon icon={SearchX} />
              <StateCard.Title>
                {t("checklist.edit.emptyFilterTitle")}
              </StateCard.Title>
              <StateCard.Description>
                {t("checklist.edit.emptyFilterDescription")}
              </StateCard.Description>
              <StateCard.Action>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleClearFilters}
                >
                  {t("checklist.edit.clearFilters")}
                </Button>
              </StateCard.Action>
            </StateCard>
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            {categories.map((category) => {
              const progress = categoryProgress.get(category.id);
              const progressPct =
                progress && progress.total > 0
                  ? (progress.approved / progress.total) * 100
                  : 0;

              return (
                <div key={category.id}>
                  <div className="sticky top-0 z-10 border-b border-border bg-background/95 px-3 py-1.5 backdrop-blur">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {category.name}
                      </span>
                      {progress && (
                        <span className="text-[10px] font-medium text-muted-foreground">
                          {t("checklist.edit.categoryProgress", {
                            completed: progress.approved,
                            total: progress.total,
                          })}
                        </span>
                      )}
                    </div>
                    {progress && progress.total > 0 && (
                      <div className="mt-1 h-0.5 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-green-500 transition-all duration-300"
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                    )}
                  </div>
                  {category.items.map((item) => {
                    const currentIndex = itemIndex;
                    itemIndex++;
                    return (
                      <div key={item.id} data-item-id={item.id}>
                        <ChecklistListItem
                          item={item}
                          isSelected={item.id === selectedItemId}
                          onClick={() => onSelectItem(item.id)}
                          index={currentIndex}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
